// src/components/SignInStep.js
//
// "Confirm it's you" (mission 009, Fable decision 009-01): the sign-in modal's
// second step after a wallet connect. The SIWE signature is requested from THIS
// tap: a popup wallet (Coinbase Smart Wallet) opens a new window for every
// request, the browser allows one popup per user activation, and the connect
// click already spent it — a request issued from an effect after the connect is
// blocked. An extension wallet is asked automatically by AuthContext; the step
// then shows "Waiting for your wallet…" and its button is live again if that
// attempt fails. The modal closes itself once the session exists (onDone).

import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useSiweAuth } from '../context/AuthContext';

const PRIMARY = 'flex h-10 w-full items-center justify-center gap-2 rounded-[10px] bg-white px-4 text-[14px] font-semibold text-black hover:bg-zinc-200 active:scale-[0.98] disabled:opacity-50';
const TEXT_BUTTON = 'h-9 px-2 text-[13px] text-zinc-400 hover:text-white disabled:opacity-40';

export const shortAddress = (a) => (a ? `${a.slice(0, 6)}…${a.slice(-4)}` : '');

/**
 * The step is shown from the moment a connected, unsigned wallet meets an open
 * modal until the modal closes — it must outlive `needed` turning false on
 * success, or it would unmount before it can close the modal.
 */
export function useSignInStep(isOpen, needed) {
  const [active, setActive] = useState(false);
  useEffect(() => {
    if (!isOpen) setActive(false);
    else if (needed) setActive(true);
  }, [isOpen, needed]);
  return [active, () => setActive(false)];
}

export default function SignInStep({ address, onDone, onUseAnother }) {
  const { ensureAuth, loginInFlight, isAuthenticated } = useSiweAuth();
  const [error, setError] = useState(null);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => { if (isAuthenticated) onDone?.(); }, [isAuthenticated]);   // eslint-disable-line react-hooks/exhaustive-deps -- fire once per sign-in

  const signIn = async () => {
    setError(null);
    try { await ensureAuth(); } catch (e) {
      setError(e?.cancelled ? 'Sign-in was cancelled. Try again.' : "Sign-in didn't complete. Try again.");
    }
  };
  const chooseAnother = async () => {
    setLeaving(true);
    try { await onUseAnother?.(); } finally { setLeaving(false); }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <h3 className="text-[17px] font-semibold tracking-tight text-white">Confirm it's you</h3>
        <p className="text-[13px] text-zinc-500">Connected as <span className="font-mono tabular-nums text-zinc-300">{shortAddress(address)}</span></p>
      </div>
      <p className="text-[14px] leading-relaxed text-zinc-300">Sign a quick message to finish signing in. No transaction, no fee.</p>
      <button type="button" disabled={loginInFlight || leaving} onClick={signIn} className={PRIMARY}>
        {loginInFlight && <Loader2 size={14} className="animate-spin" />}
        {loginInFlight ? 'Waiting for your wallet…' : 'Sign in'}
      </button>
      {error && <p className="text-[13px] text-red-300/80" role="alert">{error}</p>}
      <button type="button" disabled={leaving} onClick={chooseAnother} className={`${TEXT_BUTTON} self-center`}>Use another wallet</button>
    </div>
  );
}
